
import { supabase } from '@/integrations/supabase/client';

interface PrefetchCache {
  [key: string]: {
    data: any;
    timestamp: number;
    expiry: number;
  };
}

class CarouselPrefetchManager {
  private cache: PrefetchCache = {};
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private prefetchQueue: Set<string> = new Set();

  constructor() {
    this.registerServiceWorker();
  }

  private async registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/carousel-sw.js');
        console.log('Carousel Service Worker registered:', registration);
      } catch (error) {
        console.warn('Carousel Service Worker registration failed:', error);
      }
    }
  }

  private isExpired(timestamp: number, expiry: number): boolean {
    return Date.now() > timestamp + expiry;
  }

  private generateCacheKey(psychologistId: string): string {
    return `psychologist_${psychologistId}`;
  }

  async prefetchPsychologist(psychologistId: string): Promise<void> {
    const cacheKey = this.generateCacheKey(psychologistId);
    
    // Check if already in queue or recently cached
    if (this.prefetchQueue.has(cacheKey)) {
      return;
    }

    const cached = this.cache[cacheKey];
    if (cached && !this.isExpired(cached.timestamp, cached.expiry)) {
      return;
    }

    this.prefetchQueue.add(cacheKey);

    try {
      console.log('🔄 Prefetching psychologist data:', psychologistId);
      
      const { data, error } = await supabase
        .from('psychologists')
        .select('*')
        .eq('id', psychologistId)
        .single();

      if (error) throw error;

      // Cache the data
      this.cache[cacheKey] = {
        data,
        timestamp: Date.now(),
        expiry: this.CACHE_DURATION
      };

      // Also cache in Service Worker if available
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_CAROUSEL_DATA',
          url: `/api/psychologists/${psychologistId}`,
          data
        });
      }

      console.log('✅ Prefetched psychologist data:', psychologistId);
    } catch (error) {
      console.error('❌ Failed to prefetch psychologist:', error);
    } finally {
      this.prefetchQueue.delete(cacheKey);
    }
  }

  async prefetchNextSlides(currentIndex: number, psychologistIds: string[], lookahead = 2): Promise<void> {
    const prefetchPromises: Promise<void>[] = [];

    for (let i = 1; i <= lookahead; i++) {
      const nextIndex = currentIndex + i;
      if (nextIndex < psychologistIds.length) {
        const nextId = psychologistIds[nextIndex];
        prefetchPromises.push(this.prefetchPsychologist(nextId));
      }
    }

    await Promise.allSettled(prefetchPromises);
  }

  getCachedPsychologist(psychologistId: string): any | null {
    const cacheKey = this.generateCacheKey(psychologistId);
    const cached = this.cache[cacheKey];

    if (cached && !this.isExpired(cached.timestamp, cached.expiry)) {
      console.log('🎯 Using cached psychologist data:', psychologistId);
      return cached.data;
    }

    return null;
  }

  clearCache(): void {
    this.cache = {};
    console.log('🧹 Carousel prefetch cache cleared');
  }

  getCacheStats(): { size: number; items: string[] } {
    const validItems = Object.keys(this.cache).filter(key => {
      const cached = this.cache[key];
      return !this.isExpired(cached.timestamp, cached.expiry);
    });

    return {
      size: validItems.length,
      items: validItems
    };
  }
}

// Global instance
export const carouselPrefetch = new CarouselPrefetchManager();

// Hook for using prefetch in components
export const useCarouselPrefetch = () => {
  const prefetchNext = (currentIndex: number, psychologistIds: string[]) => {
    carouselPrefetch.prefetchNextSlides(currentIndex, psychologistIds);
  };

  const getCached = (psychologistId: string) => {
    return carouselPrefetch.getCachedPsychologist(psychologistId);
  };

  return {
    prefetchNext,
    getCached,
    clearCache: carouselPrefetch.clearCache,
    getCacheStats: carouselPrefetch.getCacheStats
  };
};
